<script setup lang="ts">
import type { DialogName } from '@/stores/useDialogStore';

import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAbility } from '@casl/vue';

import TableAlert from '@/components/alerts/TableAlert.vue';
import KBtn from '@/components/btns/KBtn.vue';
import RequestChip from '@/components/chips/RequestChip.vue';
import TablePageFooter from '@/components/footers/TablePageFooter.vue';
import ExportAsCsvIconBtn from '@/components/iconBtns/ExportAsCsvIconBtn.vue';
import ExportAsPdfIconBtn from '@/components/iconBtns/ExportAsPdfIconBtn.vue';
import AdvancedSearchSearchForm from '@/components/searchForms/AdvancedSearchSearchForm.vue';
import AdvancedSearchTable from '@/components/tables/AdvancedSearchTable.vue';
import FormWrapper from '@/components/wrappers/FormWrapper.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import TablePageShadowWrapper from '@/components/wrappers/TablePageShadowWrapper.vue';
import useCommonRouteQuery from '@/composables/routeQueries/useCommonRouteQuery';
import useCurrentDateString from '@/composables/useCurrentDateString';
import useDownloadCsv from '@/composables/useDownloadCsv';
import { useJobEditDialog } from '@/composables/useJobEditDialog';
import useAdvancedSearchView from '@/composables/views/useAdvancedSearchView';
import { useDialogStore } from '@/stores/useDialogStore';
import {
  fetchGetLookupCustomer,
  fetchGetLookupDriverByJob,
  fetchGetLookupService,
} from '@/helpers/fetchers';
import {
  customerFiltersItems,
  managerFiltersItems,
} from '@/constants/fieldItems';

const filtersItems = computed(() => {
  if (ability.can('staff', 'Job')) {
    return managerFiltersItems;
  } else {
    return customerFiltersItems;
  }
});

const ability = useAbility();
const isStaff = ability.can('staff', 'Job');

const { getLast6MthsNext6MthsRange } = useCurrentDateString();
const {
  queryDates,
  queryJobIds,
  queryFilters,
  queryStates,
  queryCustomers,
  queryServices,
  queryDrivers,
  querySerial,
  queryAddress,
  queryCombined,
  queryPayOnHold,
} = useCommonRouteQuery({
  // TODO same variables in handleResetClick()
  queryDates: getLast6MthsNext6MthsRange(),
  queryFilters: ['ACTIVE'], // TODO Connect type with filtersItems
  queryPayOnHold: undefined,
});
const { isDownloadingCSV, downloadCsv } = useDownloadCsv();

const {
  search,
  refetch,
  initialData,
  isFetchingData,
  isFetchingCountData,
  isDownloadingPDF,
  total,
  totalCount,
  results,
  resultsForCsv,
  downloadPdf,
  advancedSearchMultiSearchDialogData,
  savedFiltersDialogData,
  savingCurrentSearchDialogData,
} = useAdvancedSearchView(
  queryDates,
  queryJobIds,
  queryFilters,
  queryStates,
  queryCustomers,
  queryServices,
  queryDrivers,
  querySerial,
  queryAddress,
  queryCombined,
  queryPayOnHold,
  isStaff,
);

const route = useRoute();

const dialogStore = useDialogStore();
const jobEditDialog = useJobEditDialog();

function openAdvancedSearchMultiSearchDialog() {
  const dialogName: DialogName = 'advancedSearchMultiSearch';
  dialogStore.open(dialogName, advancedSearchMultiSearchDialogData.value);
}
function openSavedFiltersDialog() {
  const dialogName: DialogName = 'savedFilters';
  dialogStore.open(dialogName, savedFiltersDialogData.value);
}
function openSavingCurrentSearchDialog() {
  const dialogName: DialogName = 'savingCurrentSearch';
  dialogStore.open(dialogName, savingCurrentSearchDialogData.value);
}

function handleEditClick(jobId: number, jobDate: string): void {
  jobEditDialog.open(jobId, jobDate, results.value, refetch);
}
</script>

<template>
  <div :class="$style.advancedSearchView" data-testid="advancedSearchView">
    <PageContentWrapper class="mt-2">
      <FormWrapper :class="$style.formWrapper" class="mt-0">
        <div :class="$style.formDescription">
          <h2 class="g-h2">
            Search by specific criteria or open multi-search to insert a long
            list of data
          </h2>
          <KBtn
            size="lg"
            variant="text"
            prepend-icon="mdi-bookmark-outline"
            :class="$style.savedFiltersBtn"
            @click="openSavedFiltersDialog"
          >
            Saved filters
          </KBtn>
        </div>
        <AdvancedSearchSearchForm
          :initial-data="initialData"
          :loading="isFetchingData"
          :class="$style.form"
          :filters-items="filtersItems"
          :fetch-get-lookup-customer="fetchGetLookupCustomer"
          :fetch-get-lookup-service="fetchGetLookupService"
          :fetch-get-lookup-driver-by-job="fetchGetLookupDriverByJob"
          :is-staff="isStaff"
          @submit="search"
          @open-multi-search-click="openAdvancedSearchMultiSearchDialog"
        />
      </FormWrapper>
      <div
        v-if="
          route.query.dates ||
          route.query.job_ids ||
          route.query.filters ||
          route.query.states ||
          route.query.customers ||
          route.query.services ||
          route.query.drivers ||
          route.query.serial ||
          route.query.address ||
          route.query.combined
        "
        :class="$style.requests"
      >
        <div :class="$style.leftRequests">
          Requested
          <RequestChip param-title="dates" :class="$style.requestChip">
            <!-- TODO don't use route.query, use queryDates, format, etc. Because sometimes route.query.dates might be undefined but not queryDates -->
            Date range {{ route.query.dates?.toString().replace(',', '–') }}
          </RequestChip>
          <template v-for="(jobId, index) in queryJobIds" :key="jobId">
            <RequestChip
              param-title="job_ids"
              :param-value="
                (queryJobIds || [])
                  .filter((_jobId) => _jobId !== jobId)
                  .join(',')
              "
              :class="$style.requestChip"
            >
              Job ID {{ jobId }}
            </RequestChip>
          </template>
          <template v-for="(filter, index) in queryFilters" :key="filter">
            <RequestChip
              param-title="filters"
              :param-value="
                (queryFilters || [])
                  .filter((_filter) => _filter !== filter)
                  .join(',')
              "
              :class="$style.requestChip"
            >
              Filters
              {{
                filtersItems.find((filtersItem) => filtersItem.value === filter)
                  ?.title
              }}
            </RequestChip>
          </template>
          <template v-for="(state, index) in queryStates" :key="state">
            <RequestChip
              param-title="states"
              :param-value="
                (queryStates || [])
                  .filter((_state) => _state !== state)
                  .join(',')
              "
              :class="$style.requestChip"
            >
              States {{ state }}
            </RequestChip>
          </template>
          <template v-for="(customer, index) in queryCustomers" :key="customer">
            <RequestChip
              param-title="customers"
              :param-value="
                (queryCustomers || [])
                  .filter((_customer) => _customer !== customer)
                  .join(',')
              "
              :class="$style.requestChip"
            >
              Customers {{ customer }}
            </RequestChip>
          </template>
          <template v-for="(service, index) in queryServices" :key="service">
            <RequestChip
              param-title="services"
              :param-value="
                (queryServices || [])
                  .filter((_service) => _service !== service)
                  .join(',')
              "
              :class="$style.requestChip"
            >
              Services {{ service }}
            </RequestChip>
          </template>
          <template v-for="(driver, index) in queryDrivers" :key="driver">
            <RequestChip
              param-title="drivers"
              :param-value="
                (queryDrivers || [])
                  .filter((_driver) => _driver !== driver)
                  .join(',')
              "
              :class="$style.requestChip"
            >
              Drivers {{ driver }}
            </RequestChip>
          </template>
          <RequestChip param-title="serial" :class="$style.requestChip">
            Serial {{ querySerial }}
          </RequestChip>
          <RequestChip param-title="address" :class="$style.requestChip">
            Address {{ queryAddress }}
          </RequestChip>
          <RequestChip param-title="combined" :class="$style.requestChip">
            Combined {{ queryCombined }}
          </RequestChip>
          <RequestChip
            v-if="queryPayOnHold !== undefined"
            param-title="pay_on_hold"
            :class="$style.requestChip"
          >
            Pay on Hold {{ queryPayOnHold ? 'Yes' : 'No' }}
          </RequestChip>
        </div>
        <div class="text-right">
          <KBtn variant="text" :to="{ name: route.name }">
            Clear all requests
          </KBtn>
          <KBtn variant="text" @click="openSavingCurrentSearchDialog">
            Save current search
          </KBtn>
        </div>
      </div>
      <TableAlert
        v-if="!isFetchingData && total === 200"
        :class="$style.tableAlert"
      >
        Only 200 jobs are shown. Please, specify your searching request.
        <template v-if="isFetchingCountData">
          <br />
          <v-icon icon="mdi-loading" class="animate-spin"></v-icon>
        </template>
        <template v-else-if="totalCount">
          <br />
          Request result is {{ totalCount }} jobs.
        </template>
      </TableAlert>
      <TablePageShadowWrapper :class="$style.tablePageShadowWrapper">
        <AdvancedSearchTable
          :loading="isFetchingData"
          :data="results"
          :is-staff="isStaff"
          @edit-click="handleEditClick"
        />
        <TablePageFooter v-if="results?.length" :total="total" no-pages>
          <ExportAsPdfIconBtn
            class="mr-2"
            :disabled="isFetchingData || !results?.length"
            :loading="isDownloadingPDF"
            @click="downloadPdf"
          />
          <ExportAsCsvIconBtn
            :disabled="isFetchingData || !results?.length"
            :loading="isDownloadingCSV"
            @click="downloadCsv(resultsForCsv, 'advancedSearch')"
          />
        </TablePageFooter>
      </TablePageShadowWrapper>
    </PageContentWrapper>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.advancedSearchView {
}
  .formWrapper {
    padding: 0.5rem 1.5rem 0.5rem 1.5rem;
    max-width: calc(100vw - variables.$sideMainBarWidth - (variables.$pageMarginX * 2));
    margin: 2rem 0;
  }
    .formDescription {
      display: flex;
      justify-content: space-between;
    }
      .savedFiltersBtn {
        margin: 14px 0 0 4px;
      }
  .tablePageShadowWrapper {
    max-width: calc(100vw - variables.$sideMainBarWidth - (variables.$pageMarginX * 2));
  }
  .requests {
    display: flex;
    align-items: start;
    justify-content: space-between;
    margin-bottom: 14px;
  }
    .leftRequests {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }
      .requestChip:first-child {
        margin-left: 8px;
      }
      .requestChip {
        margin-right: 8px;
        margin-top: 6px;
        margin-bottom: 6px;
      }
  .requests + .tableAlert {
    margin-top: -7px;
  }
  .tableAlert {
    margin-bottom: 14px;
  }
</style>
